import clsx from "clsx";
import { useReducedMotion } from "framer-motion";
import { useId, useRef } from "react";

import { echoEyesPath, echoMarkPath } from "./geometry";
import { useEchoEyes } from "./use-echo-eyes";

/**
 * A pose the face holds until you change it. `thinking` is the one that keeps
 * moving, with or without `animate`: the eyes trade heights, a loader with a
 * face. Under reduced motion it holds one staggered frame.
 */
export type EchoExpression = "idle" | "thinking" | "listening";

interface EchoMarkProps {
  size?: number | string;
  className?: string;
  expression?: EchoExpression;
  /** Blink, glance and follow the pointer. Only for the few places where
   *  Echo is present as itself; everywhere else it is a still icon. */
  animate?: boolean;
  /** Where a still face looks, in eye units (x, y; ±1.3 max): the avatar
   *  looks at the message it wrote. */
  lookAt?: [number, number];
}

/** The body fills the box like any other icon; eyes are holes in it. */
const VIEW_BOX = "4 2.5 24 24";

const THINKING_STILL: [number, number] = [0.9, -0.9];

/**
 * Echo: a pebble tilted like a head listening, with two white eyes. Colour
 * comes from `.echo-mark`, which a caller's `text-*` utility overrides.
 */
export const EchoMark = ({
  size,
  className,
  expression = "idle",
  animate = false,
  lookAt,
}: EchoMarkProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();
  const prefersReducedMotion = useReducedMotion();

  const isTiny = typeof size === "number" && size < 20;
  const isThinking = expression === "thinking";
  const moves = animate && !prefersReducedMotion;

  const { gaze, isBlinking, lift } = useEchoEyes(ref, {
    blinks: moves,
    glances: moves && !isThinking,
    thinks: isThinking && !prefersReducedMotion,
    followsPointer: moves && !isThinking,
  });

  const eyeScale = (isTiny ? 1.3 : 1) * (expression === "listening" ? 1.28 : 1);
  const eyes = !isBlinking && {
    gaze: moves ? gaze : (lookAt ?? gaze),
    scale: eyeScale,
    lift: isThinking && prefersReducedMotion ? THINKING_STILL : lift,
  };
  const d = echoMarkPath(eyes);
  const highlight = `${id}-hl`;
  const shade = `${id}-sh`;

  return (
    <span
      ref={ref}
      aria-hidden
      className={clsx(
        "echo-mark inline-block shrink-0 align-middle",
        className,
      )}
      data-expression={expression}
      style={size !== undefined ? { width: size, height: size } : undefined}
    >
      {/* `!`: a Button restyles any svg inside it (16 px, 4 px margin), which
          shrank the mark and dropped it below the label. */}
      <svg className="m-0! size-full!" viewBox={VIEW_BOX}>
        {!isTiny && (
          <defs>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id={highlight}
              x1="5.5"
              x2="26.5"
              y1="3.5"
              y2="25.5"
            >
              <stop offset="0" stopColor="#fff" stopOpacity="0.42" />
              <stop offset="0.55" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id={shade}
              x1="5.5"
              x2="26.5"
              y1="3.5"
              y2="25.5"
            >
              <stop offset="0.5" stopColor="#000" stopOpacity="0" />
              <stop offset="1" stopColor="#000" stopOpacity="0.2" />
            </linearGradient>
          </defs>
        )}
        <path d={d} fill="currentColor" fillRule="evenodd" />
        {!isTiny && (
          <>
            <path d={d} fill={`url(#${highlight})`} fillRule="evenodd" />
            <path d={d} fill={`url(#${shade})`} fillRule="evenodd" />
          </>
        )}
        {/* Filled over the holes so they stay white whatever sits behind
            the mark. */}
        <path d={echoEyesPath(eyes)} fill="#fff" />
      </svg>
    </span>
  );
};
